import { useState } from "react";
import { Download, Linkedin, Twitter, Link2, Check, Share2 } from "lucide-react";
import { trackBlogShare, trackOutboundClick } from "@/lib/analytics";
import type { GEOAssessmentPayload } from "@/components/GEOReadinessAssessment";

interface GEOShareToolbarProps {
  slug: string;
  title: string;
  assessmentResult?: GEOAssessmentPayload | null;
}

// Helper: convert hex colour string to [r, g, b] tuple for jsPDF
function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return [r, g, b];
}

const PAGE_URL = "https://integrated.social/blog/geo-measurement-methodology-six-metrics-2026/";
const SHARE_TEXT = "The six-metric GEO measurement framework grounded in 45 peer-reviewed studies - Discoverability, Mention Probability, Citation Probability, Narrative Accuracy, Recommendation Rate, and Commercial Influence. A must-read for any team running a GEO programme.";

export default function GEOShareToolbar({ slug, title, assessmentResult }: GEOShareToolbarProps) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  async function handleDownloadPDF() {
    setDownloading(true);
    trackOutboundClick({
      link_url: PAGE_URL,
      link_text: "Download Methodology as PDF",
      link_location: "geo_share_toolbar",
      scroll_depth_at_click: 0,
    });
    try {
      // Dynamically import jsPDF to avoid bundle bloat on other pages
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      const pageW = doc.internal.pageSize.getWidth();
      const margin = 18;
      const contentW = pageW - margin * 2;
      let y = 20;

      // ── Header bar ──────────────────────────────────────────────────────────
      doc.setFillColor(0, 212, 170); // #00D4AA
      doc.rect(0, 0, pageW, 12, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text("INTEGRATED.SOCIAL  ·  GEO MEASUREMENT METHODOLOGY", margin, 8);
      doc.setTextColor(0, 0, 0);

      y = 24;

      // ── Title ───────────────────────────────────────────────────────────────
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor(15, 15, 15);
      const titleLines = doc.splitTextToSize(
        "GEO Measurement Methodology: The Six-Metric Framework",
        contentW
      );
      doc.text(titleLines, margin, y);
      y += titleLines.length * 8 + 4;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text("Grounded in a critical review of 45 peer-reviewed GEO studies (2023–2026)", margin, y);
      y += 5;
      doc.text("Author: Modi Elnadi  ·  Integrated.Social  ·  integrated.social/blog/geo-measurement-methodology-six-metrics-2026/", margin, y);
      y += 10;

      // ── Divider ─────────────────────────────────────────────────────────────
      doc.setDrawColor(220, 220, 220);
      doc.line(margin, y, pageW - margin, y);
      y += 8;

      // ── Six metrics ─────────────────────────────────────────────────────────
      const metrics = [
        {
          number: "①",
          name: "Discoverability",
          color: [0, 212, 170] as [number, number, number],
          definition: "Whether your content enters the retrieval candidate pool when an AI engine processes a relevant query. Measured as: (queries where your domain appears in retrieved context) ÷ (total sampled queries in your category).",
          evidence: "Level A–B. Schema markup, speakable specification, and structured headings are the primary interventions. The 41% PAWC uplift from Aggarwal et al. (2024) is a Discoverability-conditional result - it applies only after retrieval.",
          benchmark: "Target: ≥40% retrieval rate across your primary query set.",
        },
        {
          number: "②",
          name: "Mention Probability",
          color: [255, 140, 0] as [number, number, number],
          definition: "The proportion of AI engine responses, given that your content was retrieved, in which your brand name is explicitly surfaced. Measured as: (responses mentioning your brand) ÷ (responses where your content was in context).",
          evidence: "Level B–C. Entity salience, brand name frequency in source documents, and structured data entity declarations are the primary levers. Cross-engine variation is high.",
          benchmark: "Baseline: track across ≥20 non-branded category queries per engine.",
        },
        {
          number: "③",
          name: "Citation Probability",
          color: [124, 58, 237] as [number, number, number],
          definition: "The proportion of AI mentions that include a clickable URL attribution back to your domain. Measured as: (responses citing your URL) ÷ (responses mentioning your brand). Only 26% of cited domains appeared in both Bing Chat and Perplexity (Li & Sinnamon, 2024).",
          evidence: "Level B. Citation behaviour varies significantly by engine. Perplexity cites more aggressively than ChatGPT. Cross-engine measurement is essential.",
          benchmark: "Cross-engine: measure Perplexity, ChatGPT Search, and Google AI Mode separately.",
        },
        {
          number: "④",
          name: "Narrative Accuracy",
          color: [220, 38, 38] as [number, number, number],
          definition: "The proportion of AI-attributed claims about your brand that are factually correct and commercially aligned. Measured as: (accurate attributed claims) ÷ (total attributed claims audited). Approximately 11% of ~100,000 atomic claims in one audit were classified as insufficiently supported.",
          evidence: "Level B. A high citation rate with low Narrative Accuracy is a compliance and reputational risk, not a GEO win. Particularly critical for regulated industries.",
          benchmark: "Audit frequency: quarterly minimum; monthly for regulated sectors.",
        },
        {
          number: "⑤",
          name: "Recommendation Rate",
          color: [8, 145, 178] as [number, number, number],
          definition: "The proportion of AI responses in which the engine actively recommends your brand as a preferred solution, beyond mere mention. Measured as: (responses with active recommendation) ÷ (total responses mentioning your brand).",
          evidence: "Level C. Evidence is largely observational. Recommendation behaviour is influenced by review corpus quality, third-party authority signals, and query commercial intent.",
          benchmark: "Track separately for informational vs. commercial-intent query sets.",
        },
        {
          number: "⑥",
          name: "Commercial Influence",
          color: [5, 150, 105] as [number, number, number],
          definition: "The measurable contribution of AI visibility to qualified pipeline or revenue. Measured as: incremental leads or revenue attributable to AI-referred traffic, ideally via interrupted time-series analysis.",
          evidence: "Level D (Very Low). This is the hardest metric to isolate. Requires controlled measurement design. Anecdotal attribution ('prospect mentioned AI search') is insufficient.",
          benchmark: "Minimum: track AI-referred sessions in GA4 and their conversion rate vs. other channels.",
        },
      ];

      for (const metric of metrics) {
        // Check if we need a new page
        if (y > 240) {
          doc.addPage();
          y = 20;
        }

        // Metric header
        doc.setFillColor(...metric.color);
        doc.roundedRect(margin, y, contentW, 8, 1, 1, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(255, 255, 255);
        doc.text(`${metric.number}  ${metric.name.toUpperCase()}`, margin + 4, y + 5.5);
        y += 12;

        // Definition
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(40, 40, 40);
        doc.text("Definition:", margin, y);
        y += 4;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(70, 70, 70);
        const defLines = doc.splitTextToSize(metric.definition, contentW);
        doc.text(defLines, margin, y);
        y += defLines.length * 4 + 3;

        // Evidence
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(40, 40, 40);
        doc.text("Evidence:", margin, y);
        y += 4;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(70, 70, 70);
        const evLines = doc.splitTextToSize(metric.evidence, contentW);
        doc.text(evLines, margin, y);
        y += evLines.length * 4 + 3;

        // Benchmark
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(40, 40, 40);
        doc.text("Benchmark:", margin, y);
        y += 4;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(70, 70, 70);
        const bmLines = doc.splitTextToSize(metric.benchmark, contentW);
        doc.text(bmLines, margin, y);
        y += bmLines.length * 4 + 8;

        // Separator
        doc.setDrawColor(230, 230, 230);
        doc.line(margin, y - 4, pageW - margin, y - 4);
      }

      // ── Minimum Reproducible Protocol ───────────────────────────────────────
      if (y > 200) { doc.addPage(); y = 20; }
      y += 4;
      doc.setFillColor(15, 15, 15);
      doc.rect(margin, y, contentW, 8, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text("MINIMUM REPRODUCIBLE MEASUREMENT PROTOCOL", margin + 4, y + 5.5);
      y += 14;

      const protocol = [
        "1. Define a query set of ≥20 non-branded category queries per target engine.",
        "2. Sample each engine 3–5 times per query using paraphrase variants to estimate variance.",
        "3. Record: (a) whether your domain appeared in retrieved context, (b) whether your brand was mentioned, (c) whether your URL was cited, (d) whether the claim was accurate, (e) whether a recommendation was made.",
        "4. Compute each metric with its correct denominator (see definitions above).",
        "5. Repeat monthly. Use 3-month rolling averages to smooth prompt-sensitivity noise.",
        "6. Report cross-engine: never aggregate Perplexity and ChatGPT into a single score.",
        "7. For Commercial Influence: use GA4 source/medium segmentation to isolate AI-referred sessions and track their conversion rate against a control period.",
      ];

      for (const step of protocol) {
        if (y > 270) { doc.addPage(); y = 20; }
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(40, 40, 40);
        const stepLines = doc.splitTextToSize(step, contentW);
        doc.text(stepLines, margin, y);
        y += stepLines.length * 4.5 + 3;
      }

      // ── Footer ───────────────────────────────────────────────────────────────
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFillColor(245, 245, 245);
        doc.rect(0, doc.internal.pageSize.getHeight() - 10, pageW, 10, "F");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `© 2026 Integrated.Social  ·  integrated.social  ·  Page ${i} of ${pageCount}`,
          margin,
          doc.internal.pageSize.getHeight() - 3.5
        );
        doc.text(
          "Based on: Aggarwal et al. (2024), Li & Sinnamon (2024), and 43 additional peer-reviewed GEO studies.",
          margin,
          doc.internal.pageSize.getHeight() - 7
        );
      }

      // ── Personalised Score Page (only if assessment was completed) ────────────
      if (assessmentResult) {
        doc.addPage();
        let sy = 0;

        // Header bar
        doc.setFillColor(0, 212, 170);
        doc.rect(0, 0, pageW, 12, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        doc.text("INTEGRATED.SOCIAL  ·  YOUR PERSONALISED GEO READINESS REPORT", margin, 8);
        sy = 24;

        // Score headline
        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        // Band colour as RGB
        const bandRgb = hexToRgb(assessmentResult.bandColor);
        doc.setTextColor(bandRgb[0], bandRgb[1], bandRgb[2]);
        doc.text(`${assessmentResult.totalScore} / 18`, margin, sy);
        sy += 10;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.setTextColor(15, 15, 15);
        doc.text(assessmentResult.band, margin, sy);
        sy += 6;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        const descLines = doc.splitTextToSize(assessmentResult.bandDescription, contentW);
        doc.text(descLines, margin, sy);
        sy += descLines.length * 4.5 + 10;

        // Divider
        doc.setDrawColor(220, 220, 220);
        doc.line(margin, sy, pageW - margin, sy);
        sy += 8;

        // Metric heatmap
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(40, 40, 40);
        doc.text("METRIC-BY-METRIC READINESS HEATMAP", margin, sy);
        sy += 7;

        const barMaxW = contentW - 60;
        for (const r of assessmentResult.results) {
          const rgb = hexToRgb(r.accentColor);
          const barW = (r.score / 3) * barMaxW;
          const bandLabel = r.label;

          // Metric label
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(40, 40, 40);
          doc.text(r.metricLabel, margin, sy);

          // Score fraction
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);
          doc.text(`${r.score}/3  ${bandLabel}`, pageW - margin - 30, sy);

          sy += 4;

          // Background track
          doc.setFillColor(230, 230, 230);
          doc.roundedRect(margin, sy, barMaxW, 5, 1, 1, "F");

          // Filled bar
          if (barW > 0) {
            doc.setFillColor(rgb[0], rgb[1], rgb[2]);
            doc.roundedRect(margin, sy, barW, 5, 1, 1, "F");
          }

          sy += 10;

          // Advice
          doc.setFont("helvetica", "italic");
          doc.setFontSize(7.5);
          doc.setTextColor(100, 100, 100);
          const adviceLines = doc.splitTextToSize(r.advice, contentW);
          doc.text(adviceLines, margin, sy);
          sy += adviceLines.length * 3.5 + 5;
        }

        // CTA
        sy += 4;
        doc.setFillColor(0, 212, 170);
        doc.roundedRect(margin, sy, contentW, 14, 2, 2, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.text("Ready to improve your GEO Readiness Score?", margin + 4, sy + 5.5);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text("Book a free 30-min GEO strategy call: integrated.social/contact/", margin + 4, sy + 10.5);
      }

      doc.save("GEO-Measurement-Methodology-Integrated-Social.pdf");
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      setDownloading(false);
    }
  }

  function handleCopyLink() {
    navigator.clipboard.writeText(PAGE_URL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
    trackBlogShare({ platform: "copy_link", post_slug: slug, post_title: title });
  }

  return (
    <div className="my-8 rounded-2xl border border-[#00D4AA]/30 bg-[#00D4AA]/5 p-5" id="geo-share-toolbar">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Left: label */}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest text-[#00D4AA] mb-0.5">Share this methodology</p>
          <p className="text-sm text-muted-foreground leading-snug">
            Found this framework useful? Download the PDF reference card or share with your team.
          </p>
        </div>

        {/* Right: buttons */}
        <div className="flex flex-wrap gap-2 shrink-0">
          {/* Download PDF */}
          <button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#00D4AA] text-black font-semibold text-sm hover:bg-[#00bfa0] disabled:opacity-60 disabled:cursor-wait transition-colors shadow-md shadow-[#00D4AA]/20"
          >
            <Download className="w-4 h-4" />
            {downloading ? "Generating…" : "Download PDF"}
          </button>

          {/* LinkedIn */}
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(PAGE_URL)}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on LinkedIn"
            onClick={() => trackBlogShare({ platform: "linkedin", post_slug: slug, post_title: title })}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0A66C2] text-white font-semibold text-sm hover:bg-[#0958a8] transition-colors"
          >
            <Linkedin className="w-4 h-4" />
            <span className="hidden sm:inline">LinkedIn</span>
          </a>

          {/* X / Twitter */}
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(PAGE_URL)}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on X"
            onClick={() => trackBlogShare({ platform: "twitter", post_slug: slug, post_title: title })}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-black text-white font-semibold text-sm hover:bg-zinc-800 transition-colors"
          >
            <Twitter className="w-4 h-4" />
            <span className="hidden sm:inline">X</span>
          </a>

          {/* WhatsApp */}
          <a
            href={`https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${PAGE_URL}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on WhatsApp"
            onClick={() => trackBlogShare({ platform: "whatsapp", post_slug: slug, post_title: title })}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#25D366] text-white font-semibold text-sm hover:bg-[#1ebe5a] transition-colors"
          >
            <Share2 className="w-4 h-4" />
            <span className="hidden sm:inline">WhatsApp</span>
          </a>

          {/* Copy link */}
          <button
            onClick={handleCopyLink}
            aria-label="Copy link"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-foreground font-semibold text-sm hover:bg-muted transition-colors"
          >
            {copied ? <Check className="w-4 h-4 text-[#00D4AA]" /> : <Link2 className="w-4 h-4" />}
            <span className="hidden sm:inline">{copied ? "Copied!" : "Copy link"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
